import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode, createElement } from 'react'
import { useAuth } from './AuthProvider'

/**
 * The integrator the admin is CURRENTLY operating within. For scoped
 * admins this is always their own tenant (immutable). For system admins
 * (`reset` slug) it defaults to `reset` but can be overridden via
 * `setActive(id, name)` so a sys-admin can "view as" any integrator
 * without logging out.
 *
 * The override is persisted in sessionStorage so a page refresh keeps
 * context; closing the tab clears it. Writes trigger a React Query
 * invalidation of every scoped key via the key prefix convention
 * (`['scope', activeIntegratorId, …]`) — pages that participate in the
 * override include `activeIntegratorId` in their query key.
 */

interface ActiveIntegratorValue {
  integratorId: string
  integratorName: string
  isOverride: boolean
  /** Only sys admin may call these — guarded at the setter. */
  setActive: (id: string, name: string) => void
  clearActive: () => void
}

const Ctx = createContext<ActiveIntegratorValue | null>(null)
const SS_ID   = 'admin:activeIntegratorId'
const SS_NAME = 'admin:activeIntegratorName'

export function ActiveIntegratorProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth()

  const [override, setOverride] = useState<{ id: string; name: string } | null>(() => {
    try {
      const id = sessionStorage.getItem(SS_ID)
      const name = sessionStorage.getItem(SS_NAME)
      return id && name ? { id, name } : null
    } catch { return null }
  })

  // If the session changes (login/logout) or the signed-in tenant is
  // not the override's own tenant, drop any stale override — a scoped
  // admin cannot carry one.
  useEffect(() => {
    if (!session) {
      setOverride(null)
      return
    }
    if (!session.isSystemAdmin && override) {
      setOverride(null)
      try { sessionStorage.removeItem(SS_ID); sessionStorage.removeItem(SS_NAME) } catch { /* storage disabled */ }
    }
  }, [session, override])

  const setActive = useCallback((id: string, name: string) => {
    if (!session?.isSystemAdmin) return
    setOverride({ id, name })
    try { sessionStorage.setItem(SS_ID, id); sessionStorage.setItem(SS_NAME, name) } catch { /* storage disabled */ }
  }, [session?.isSystemAdmin])

  const clearActive = useCallback(() => {
    setOverride(null)
    try { sessionStorage.removeItem(SS_ID); sessionStorage.removeItem(SS_NAME) } catch { /* storage disabled */ }
  }, [])

  const value = useMemo<ActiveIntegratorValue | null>(() => {
    if (!session) return null
    const id   = override?.id   ?? session.integratorId
    const name = override?.name ?? session.integratorName
    return { integratorId: id, integratorName: name, isOverride: !!override, setActive, clearActive }
  }, [session, override, setActive, clearActive])

  return createElement(Ctx.Provider, { value }, children)
}

export function useActiveIntegrator(): ActiveIntegratorValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useActiveIntegrator must be used inside ActiveIntegratorProvider (within AuthProvider)')
  return v
}
